import Config from "../_config/config";
import myAxios from "./axiositeachWrapper";

const BASE = `${Config.nodeSiteUrl}v1`;

export interface ProductListItem {
  id: number;
  name: string | null;
  productCode: string | null;
  imageUrl: string | null;
  category: string | null;
  domain: string | null;
  ownership: string | null;
  partnerId: number | null;
  partnerName: string | null;
  price: number | string | null;
  priceLabel: string | null;
  cmsId: number | null;
  publishStatus: number | null;
  cmsUpdatedDate: string | null;
}

export interface DeliveryFormatOption {
  id: number;
  name: string;
}

export interface PartnerListItem {
  id: number;
  name: string | null;
  shortName: string | null;
  url: string | null;
  linkedInUrl: string | null;
  instagramUrl: string | null;
  spocName: string | null;
  spocEmail: string | null;
  spocPhone: string | null;
  contactPerson: string | null;
  contactEmail: string | null;
  telephone: string | null;
  location: string | null;
  displayName: string | null;
}

export interface ProductMasterPayload {
  name?: string;
  productCode?: string | null;
  category?: string | null;
  domain?: string | null;
  ownership?: string | null;
  partnerId?: number | null;
  batchStartDate?: string | null;
  batchEndDate?: string | null;
  principalCoordinator?: string | null;
  jtCoordinator?: string | null;
  status?: string | null;
  price?: number | null;
  priceLabel?: string | null;
  productLaunchUrl?: string | null;
  paymentPageUrl?: string | null;
  imageUrl?: string | null;
  marketplaceEnabled?: boolean;
  deliveryFormatIds?: number[];
  courseIds?: number[];
  programIds?: number[];
  productCategoryTagIds?: number[];
  batchIds?: number[];
  installmentTemplateId?: number | null;
}

/** A batch mapped to a product, as returned by the product details endpoint. */
export interface ProductBatch {
  id: number;
  name: string | null;
  batchCode: string | null;
  schoolId: number | null;
  schoolName: string | null;
  programId: number | null;
  programName: string | null;
  courseId: number | null;
  courseName: string | null;
  yearId: number | null;
  yearName: string | null;
  /** Comma-separated instructor names, or null when none mapped. */
  instructors: string | null;
}

export interface CourseOption {
  id: number;
  name: string;
}

export interface ProgramOption {
  id: number;
  name: string;
}

export interface ProductCategoryOption {
  id: number;
  name: string;
  typeId?: number;
  typeName?: string;
}

export interface ProductImportRow {
  // ── Master fields (product_master) ───────────────────────────────────
  name: string;
  productCode?: string | null;
  subtitle?: string | null;
  category?: string | null;
  domain?: string | null;
  ownership?: string | null;
  partnerName?: string | null;
  batchStartDate?: string | null;
  batchEndDate?: string | null;
  principalCoordinator?: string | null;
  jtCoordinator?: string | null;
  status?: string | null;
  price?: number | null;
  priceLabel?: string | null;
  modes?: string | null;
  productLaunchUrl?: string | null;
  paymentPageUrl?: string | null;
  providerSchoolName?: string | null;
  marketplaceEnabled?: boolean | null;
  // ── CMS marketing fields (product_cms) ───────────────────────────────
  description?: string | null;
  language?: string | null;
  level?: string | null;
  durationHours?: number | null;
  lessonCount?: number | null;
  certificateOffered?: boolean | null;
  heroVideoUrl?: string | null;
  heroBannerUrl?: string | null;
  certificateImageUrl?: string | null;
  brochureUrl?: string | null;
  tags?: string[] | null;
  whatYouWillLearn?: string[] | null;
  requirements?: string[] | null;
  targetAudience?: string[] | null;
  instructorName?: string | null;
  instructorTitle?: string | null;
  instructorPhotoUrl?: string | null;
  instructorBio?: string | null;
  // Array-of-object fields. Each cell uses one row per entry (newline-
  // separated within the cell), pipe-separated fields per row.
  //   Testimonials: Name | Role | Rating | Quote
  //   FAQs:         Question | Answer
  //   Highlights:   Title | Description
  //   Tools:        Name | Logo URL
  testimonials?: Array<{ name: string; role?: string; rating?: number; quote: string }> | null;
  faqs?: Array<{ q: string; a: string }> | null;
  highlights?: Array<{ title: string; description?: string }> | null;
  tools?: Array<{ name: string; logoUrl?: string }> | null;
  publishStatus?: boolean | null;
  // ── Micro-credentials (writes badge_eligibility ScopeType='course' when product has a CourseId) ──
  // Comma-separated badge identifiers; each can be a badge Name, Code, or UUID.
  badges?: string[] | null;
  // ── Partner enrichment (writes back to partner_master) ───────────────
  partnerSpocName?: string | null;
  partnerSpocEmail?: string | null;
  partnerSpocPhone?: string | null;
  partnerLinkedInUrl?: string | null;
  partnerInstagramUrl?: string | null;
}

export interface PartnerImportRow {
  name: string;
  spocName?: string | null;
  spocEmail?: string | null;
  spocPhone?: string | null;
  linkedInUrl?: string | null;
  instagramUrl?: string | null;
  url?: string | null;
  shortName?: string | null;
}

export type ImportMode = "skip" | "upsert";

export interface ImportResult {
  success: boolean;
  inserted: number;
  updated: number;
  skipped: Array<{ rowIndex: number; reason: string; existingId?: number; productCode?: string | null; name?: string }>;
  errors: Array<{ rowIndex: number; message: string }>;
}

export interface FaqItem {
  q: string;
  a: string;
}

export interface TestimonialItem {
  name: string;
  role?: string;
  photoUrl?: string;
  quote: string;
  rating?: number;
}

export interface InstructorInfo {
  name: string;
  title?: string;
  photoUrl?: string;
  bio?: string;
  credentials?: string[];
}

export interface HighlightItem {
  title: string;
  description?: string;
}

export interface ToolItem {
  name: string;
  logoUrl?: string;
}

export interface EmiPlan {
  months: number;
  perMonth: number;
}

export interface FeesInfo {
  emiPlans?: EmiPlan[];
  scholarship?: string;
  groupDiscountPct?: number;
}

export type CurriculumLessonType =
  | "video"
  | "reading"
  | "case-study"
  | "live"
  | "project"
  | "quiz";

export interface CurriculumLesson {
  title: string;
  type?: CurriculumLessonType;
  durationLabel?: string;
}

export interface CurriculumModule {
  title: string;
  subtitle?: string;          // e.g. "Week 1–2"
  durationLabel?: string;     // free-text, e.g. "6 hours"
  summary?: string;
  outcomes?: string[];
  lessons?: CurriculumLesson[];
}

export interface ProductCmsPayload {
  subtitle?: string | null;
  heroVideoUrl?: string | null;
  heroBannerUrl?: string | null;
  language?: string | null;
  level?: string | null;
  durationHours?: number | null;
  lessonCount?: number | null;
  certificateOffered?: boolean;
  ratingAvg?: number | null;
  ratingCount?: number | null;
  enrolledCount?: number | null;
  whatYouWillLearn?: string[];
  requirements?: string[];
  targetAudience?: string[];
  instructor?: InstructorInfo | null;
  testimonials?: TestimonialItem[];
  faq?: FaqItem[];
  tags?: string[];
  highlights?: HighlightItem[];
  tools?: ToolItem[];
  fees?: FeesInfo | null;
  curriculum?: CurriculumModule[];
  skills?: string[];
  certificateImageUrl?: string | null;
  brochureUrl?: string | null;
  publishStatus?: boolean;
  // ── Publishing & Marketplace (live on product_master) ───────────────
  price?: number | null;
  marketplaceEnabled?: boolean;
}

export interface ProductDetailsResponse extends ProductCmsPayload {
  id: number;
  name: string | null;
  productCode: string | null;
  imageUrl: string | null;
  description: string | null;
  fullDescription: string | null;
  previewLearningModuleId: number | null;
  courseId: number | null;
  programId: number | null;
  category: string | null;
  domain: string | null;
  ownership: string | null;
  partnerId: number | null;
  partnerName: string | null;
  batchStartDate: string | null;
  batchEndDate: string | null;
  principalCoordinator: string | null;
  jtCoordinator: string | null;
  status: string | null;
  productLaunchUrl: string | null;
  paymentPageUrl: string | null;
  deliveryFormats: Array<{ id: number; name: string }> | null;
  courses: Array<{ id: number; name: string }> | null;
  programs: Array<{ id: number; name: string }> | null;
  productCategories: Array<{ id: number; name: string }> | null;
  batches: ProductBatch[] | null;
  installmentTemplateId: number | null;
  installmentTemplateName: string | null;
  activeSubscriberCount: number;
}

export type CmsAssetKind = "certificate" | "brochure" | "logo" | "banner";

export interface UploadAssetResponse {
  url: string;
  filename: string;
  size: number;
  mimetype: string;
  kind: string;
}

export interface AccessScope {
  isAdmin: boolean;
  schoolIds: number[] | null;
  partnerIds: number[] | null;
  partners: Array<{ id: number; name: string }>;
  schools: Array<{ id: number; name: string }>;
}

export interface PartnerContact {
  userId: number;
  userName: string;
  email: string | null;
  fullName: string | null;
  activeStatus: number | null;
  mappedAt: string | null;
  roles: string | null;
}

export interface ProductCampaign {
  id: number;
  name: string | null;
  startDate: string | null;
  endDate: string | null;
  budget: number | null;
  actualSpend: number | null;
  platform: string | null;
  status: string | null;
  trackingCode: string | null;
  webhookVerifyToken: string | null;
  facebookPageId: string | null;
  facebookFormId: string | null;
  description: string | null;
  createdDate: string | null;
  externalCampaignId: string | null;
  metaImpressions: number | null;
  metaClicks: number | null;
  metaLastSyncedAt: string | null;
  visits: number;
  uniqueVisitors: number;
  brochureLeads: number;
  enquiries: number;
  conversions: number;
  revenue: number;
}

export interface ProductCampaignPayload {
  name?: string;
  startDate?: string | null;
  endDate?: string | null;
  budget?: number | null;
  actualSpend?: number | null;
  platform?: string;
  status?: string;
  description?: string | null;
  facebookPageId?: string | null;
  facebookFormId?: string | null;
  externalCampaignId?: string | null;
}

class ProductCmsService {
  listProducts = (search?: string) =>
    myAxios.get<any>(`${BASE}/admin/products`, {
      params: search ? { search } : {},
    });

  getAccessScope = () =>
    myAxios.get<any>(`${BASE}/admin/access-scope`);

  // ── Partner contacts (external provider users with login access) ──
  listPartnerContacts = (partnerId: number) =>
    myAxios.get<any>(`${BASE}/admin/partners/${partnerId}/contacts`);
  createPartnerContact = (
    partnerId: number,
    body: {
      userName: string;
      fullName: string;
      email?: string;
      password?: string;
      telNumber?: string;
      roleIds?: number[];
    },
  ) =>
    myAxios.post<any>(`${BASE}/admin/partners/${partnerId}/contacts`, body);
  removePartnerContact = (partnerId: number, userId: number) =>
    myAxios.delete<any>(`${BASE}/admin/partners/${partnerId}/contacts/${userId}`);

  listDeliveryFormats = () =>
    myAxios.get<any>(`${BASE}/admin/delivery-formats`);

  listCourses = () => myAxios.get<any>(`${BASE}/courses`);

  listPrograms = () => myAxios.get<any>(`${BASE}/academic/programs-list`);

  listProductCategories = () =>
    myAxios.get<any>(`${BASE}/admin/product-categories`);

  listPartners = (search?: string) =>
    myAxios.get<any>(`${BASE}/admin/partners`, {
      params: search ? { search } : {},
    });

  createPartner = (payload: Partial<PartnerListItem>) =>
    myAxios.post<any>(`${BASE}/admin/partners`, payload);

  updatePartner = (partnerId: number, payload: Partial<PartnerListItem>) =>
    myAxios.put<any>(`${BASE}/admin/partners/${partnerId}`, payload);

  importPartners = (mode: ImportMode, rows: PartnerImportRow[]) =>
    myAxios.post<any>(`${BASE}/admin/partners/import`, { mode, rows });

  createProduct = (payload: ProductMasterPayload) =>
    myAxios.post<any>(`${BASE}/admin/products`, payload);

  updateProduct = (productId: number, payload: ProductMasterPayload) =>
    myAxios.put<any>(`${BASE}/admin/products/${productId}`, payload);

  importProducts = (mode: ImportMode, rows: ProductImportRow[]) =>
    myAxios.post<any>(`${BASE}/admin/products/import`, { mode, rows });

  getProductDetails = (productId: number) =>
    myAxios.get<any>(`${BASE}/products/${productId}/details`);

  // ── Product-level marketing campaigns (Campaigns tab) ──
  listProductCampaigns = (productId: number) =>
    myAxios.get<any>(`${BASE}/admin/products/${productId}/campaigns`);

  createProductCampaign = (productId: number, payload: ProductCampaignPayload) =>
    myAxios.post<any>(`${BASE}/admin/products/${productId}/campaigns`, payload);

  updateProductCampaign = (
    productId: number,
    campaignId: number,
    payload: ProductCampaignPayload,
  ) =>
    myAxios.put<any>(
      `${BASE}/admin/products/${productId}/campaigns/${campaignId}`,
      payload,
    );

  deleteProductCampaign = (productId: number, campaignId: number) =>
    myAxios.delete<any>(
      `${BASE}/admin/products/${productId}/campaigns/${campaignId}`,
    );

  syncMetaInsights = (productId: number) =>
    myAxios.post<any>(`${BASE}/admin/products/${productId}/campaigns/sync-meta`, {});

  getMetaConnection = () =>
    myAxios.get<any>(`${BASE}/tenant-config/meta-marketing`);

  saveMetaConnection = (payload: { adAccountId: string; accessToken?: string }) =>
    myAxios.put<any>(`${BASE}/tenant-config/meta-marketing`, payload);

  saveCms = (productId: number, payload: ProductCmsPayload) =>
    myAxios.put<any>(`${BASE}/admin/products/${productId}/cms`, payload);

  uploadAsset = (productId: number, kind: CmsAssetKind, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return myAxios.post<any>(
      `${BASE}/admin/products/${productId}/cms/upload`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        params: { kind },
      },
    );
  };
}

export default new ProductCmsService();
